import type { SALARY_CALCULATION_OPTION } from 'cpool-constants';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { HeaderNotification } from './HeaderNotification';
import type { SchemaCreditRequestForm } from '../schemas/creditRequestForm.schema';

type Props = {
    hasTwoApplicants: boolean;
};

const SNIPPET_FLAGS = [
    { field: 'excludeApplicant', label: 'EXCLUDE_APPLICANT' },
    { field: 'rentFree', label: 'RENT_FREE' },
    { field: 'withoutRentIncome', label: 'WITHOUT_RENT_INCOME' },
    { field: 'withoutMaintenanceIncome', label: 'WITHOUT_MAINTENANCE_INCOME' },
    { field: 'withoutSidejob', label: 'WITHOUT_SIDE_JOB' },
    { field: 'withoutOtherIncome', label: 'WITHOUT_OTHER_INCOME' },
] as const;

export const DataSharingNotifications: React.FC<Props> = ({ hasTwoApplicants }) => {
    const { t } = useTranslation();
    const dataSnippets = useWatch({ name: 'dataSnippets' }) as SchemaCreditRequestForm['dataSnippets'];
    const applicant1 = dataSnippets?.applicant1;
    const applicant2 = dataSnippets?.applicant2;
    const salaryModeApplicant1 = useWatch({ name: 'dataSnippets.applicant1.salaryMode' }) as SALARY_CALCULATION_OPTION;
    const salaryModeApplicant2 = useWatch({ name: 'dataSnippets.applicant2.salaryMode' }) as SALARY_CALCULATION_OPTION;

    return (
        <div className="flex flex-wrap justify-end gap-2">
            <HeaderNotification
                displayName={t('BANK_CREDIT_REQUEST.DATA_SHARING.SALARY')}
                value={t(
                    `BANK_CREDIT_REQUEST.DATA_SHARING.HEADER_NOTIFICATIONS.SALARY_CALCULATION.${salaryModeApplicant1}`,
                )}
                applicant={1}
                hasTwoApplicants={hasTwoApplicants}
            />
            {SNIPPET_FLAGS.map(({ field, label }) => (
                <HeaderNotification
                    key={field}
                    displayName={t(`BANK_CREDIT_REQUEST.DATA_SHARING.${label}`)}
                    value={applicant1?.[field] === true}
                    applicant={1}
                    hasTwoApplicants={hasTwoApplicants}
                />
            ))}
            {hasTwoApplicants && (
                <>
                    <HeaderNotification
                        displayName={t('BANK_CREDIT_REQUEST.DATA_SHARING.SALARY')}
                        value={t(
                            `BANK_CREDIT_REQUEST.DATA_SHARING.HEADER_NOTIFICATIONS.SALARY_CALCULATION.${salaryModeApplicant2}`,
                        )}
                        applicant={2}
                    />
                    {SNIPPET_FLAGS.map(({ field, label }) => (
                        <HeaderNotification
                            key={field}
                            displayName={t(`BANK_CREDIT_REQUEST.DATA_SHARING.${label}`)}
                            value={applicant2?.[field] === true}
                            applicant={2}
                        />
                    ))}
                </>
            )}
        </div>
    );
};
